<template>
    <div class="flex flex-col gap-3 min-h-[calc(100dvh-5rem)]">
        <!-- Logo -->
        <div class="flex justify-center pt-4 pb-4">
            <img :src="logoUrl" alt="Bzuco" class="h-[4.5rem] object-contain" />
        </div>

        <!-- User / Environment row -->
        <div class="flex items-center justify-between px-2 text-sm gap-3">
            <span class="opacity-80 truncate">{{ userLabel }}</span>
            <span
                class="font-bold tracking-wider flex-shrink-0"
                :class="{
                    'text-warning': envLabel === 'TEST',
                    'text-success': envLabel === 'PROD',
                    'text-sky-400': envLabel === 'DEV'
                }"
            >
                {{ envLabel }}
            </span>
        </div>

        <!-- Logged out: login + test scanner -->
        <template v-if="!auth.user">
            <AppButton block large :disabled="!network.isOnline" @click="goLogin">
                {{ t('home.login') }}
            </AppButton>
            <AppButton variant="gray" block large @click="startTest">
                {{ t('menu.test') }}
            </AppButton>
            <AppButton variant="teal" block large class="mt-auto" @click="goConfigScanner">
                <ScanLine class="w-5 h-5 mr-2" />
                {{ t('home.scanConfig') }}
            </AppButton>
        </template>

        <template v-if="auth.user">
            <!-- Last scanned event/zone -->
            <button
                v-if="hasLastSelection"
                class="bg-success text-white rounded-lg px-4 py-8 text-center font-medium shadow-md hover:brightness-110 transition-colors flex flex-col gap-2"
                @click="goLastScanner"
            >
                <span class="text-lg" v-html="lastMode === 'zones' ? t('home.lastZones') : t('home.lastEvents')" />
                <span class="text-base opacity-90 font-normal">{{ lastSelectionLabel }}</span>
            </button>

            <!-- Two-up: events / zones -->
            <div class="grid grid-cols-2 gap-3">
                <button
                    class="bg-purple-600 text-white rounded-lg py-12 px-3 text-center text-lg font-medium shadow-md hover:brightness-110 transition-colors disabled:opacity-40"
                    :disabled="!network.isOnline"
                    @click="goEvents"
                    v-html="t('home.checkEvents')"
                />
                <button
                    class="bg-sky-500 text-white rounded-lg py-12 px-3 text-center text-lg font-medium shadow-md hover:brightness-110 transition-colors disabled:opacity-40"
                    :disabled="!network.isOnline"
                    @click="goZones"
                    v-html="t('home.checkZones')"
                />
            </div>

            <!-- Config QR + Settings / logout pinned to bottom -->
            <div class="mt-auto flex flex-col gap-3">
                <AppButton variant="teal" block large @click="goConfigScanner">
                    <ScanLine class="w-5 h-5 mr-2" />
                    {{ t('home.scanConfig') }}
                </AppButton>
                <div class="grid grid-cols-2 gap-3">
                    <AppButton variant="gray" block large @click="goSettings">
                        {{ t('menu.settings') }}
                    </AppButton>
                    <AppButton variant="danger" block large @click="logout">
                        {{ t('menu.logout') }}
                    </AppButton>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useRouter } from 'vue-router'
    import { useAuthStore } from '@/stores/auth'
    import { useTicketsStore } from '@/stores/tickets'
    import { useNetworkStore } from '@/stores/network'
    import { useEventsStore } from '@/stores/events'
    import { useI18n } from 'vue-i18n'
    import { initSound } from '@/services/sound'
    import AppButton from '@/components/ui/AppButton.vue'
    import logoUrl from '@/assets/logo-home-bzuco.png'
    import { ScanLine } from 'lucide-vue-next'

    const auth = useAuthStore()
    const tickets = useTicketsStore()
    const network = useNetworkStore()
    const events = useEventsStore()
    const router = useRouter()
    const { t } = useI18n()

    const userLabel = computed(() => {
        if (!auth.user) return ''
        const name = auth.user.name
        const username = auth.username
        return username ? `${name} (${username})` : name
    })

    const envLabel = computed<'TEST' | 'PROD' | 'DEV'>(() => {
        const host = window.location.hostname
        if (host === 'inspector-test.bzuco.cz') return 'TEST'
        if (host === 'inspector.bzuco.cz') return 'PROD'
        return 'DEV'
    })

    const hasLastSelection = computed(
        () => tickets.tickets.length > 0 && (events.activeEvents.length > 0 || events.activeZones.length > 0)
    )

    const lastMode = computed<'zones' | 'events' | null>(() => {
        if (events.activeZones.length > 0) return 'zones'
        if (events.activeEvents.length > 0) return 'events'
        return null
    })

    const lastSelectionLabel = computed(() => {
        if (lastMode.value === 'zones') {
            const names = events.activeZones
                .map(zid => events.zones.find(z => Number(z.id) === zid)?.name ?? '')
                .filter(Boolean)
            return names.join(', ')
        }
        if (lastMode.value === 'events') {
            const names = events.activeEvents
                .map(id => events.events.find(e => e.id === id)?.name ?? '')
                .filter(Boolean)
            return names.join(', ')
        }
        return ''
    })

    function goLogin() {
        void router.push({ name: 'login' })
    }
    function goEvents() {
        void router.push({ name: 'events' })
    }
    function goZones() {
        void router.push({ name: 'zones' })
    }
    function goLastScanner() {
        initSound()
        void router.push({ name: 'scanner' })
    }
    function startTest() {
        initSound()
        void router.push({ name: 'test-scanner' })
    }
    function goConfigScanner() {
        void router.push({ name: 'config-scanner' })
    }
    function goSettings() {
        void router.push({ name: 'settings' })
    }
    async function logout() {
        try {
            await auth.logoutUser()
        } catch {
            // ignore
        }
    }
</script>
